import { useState, useCallback, useEffect } from 'react'
import {
  IConfigurableBuilderTest,
  ICreateConfigurableBuilderTestInput,
  IUpdateConfigurableBuilderTestInput,
  IConfigurableBuilderTestResult,
  IRunConfigurableBuilderTestsInput,
} from 'types/graphqlTypes'
import { useConfigurableBuilderTestsQuery } from '../graphql/ConfigurableBuilderTests'
import { useCreateConfigurableBuilderTestMutation } from '../graphql/CreateConfigurableBuilderTestMutation'
import { useRunConfigurableBuilderTestsMutation } from '../graphql/RunConfigurableBuilderTestsMutation'
import { useUpdateConfigurableBuilderTestMutation } from '../graphql/UpdateConfigurableBuilderTestMutation'

interface IUseConfigurableBuilderTestArgs {
  builderId?: string
  displayUserFeedback(message: string, level: 'error' | 'info'): void
  setCurrentBuilderTest(currentBuilderTest: IConfigurableBuilderTest): void
}

export const useConfigurableBuilderTest = ({
  builderId,
  displayUserFeedback,
  setCurrentBuilderTest,
}: IUseConfigurableBuilderTestArgs) => {
  const [createConfigurableBuilderTestMutation] = useCreateConfigurableBuilderTestMutation()
  const [updateConfigurableBuilderTestMutation] = useUpdateConfigurableBuilderTestMutation()
  const [runConfigurableBuilderTestsMutation] = useRunConfigurableBuilderTestsMutation()

  const { data: testData, refetch: refetchTestData } = useConfigurableBuilderTestsQuery({
    skip: !builderId,
    variables: {
      // @ts-ignore : `skip` will avoid running query when builderId is null.
      id: builderId,
    },
  })

  const [configurableBuilderTests, setConfigurableBuilderTests] = useState<
    IConfigurableBuilderTest[]
  >([])

  const [latestTestResults, setLatestTestResults] = useState<IConfigurableBuilderTestResult[]>([])
  const [testsAreRunning, setTestsAreRunning] = useState<boolean>(false)

  // invalidate some test results. (invoked when test inputs are updated.)
  const invalidateLatestTestResults = (id?: string): void => {
    let updatedTestResults: IConfigurableBuilderTestResult[] = []
    if (id !== undefined) {
      updatedTestResults = latestTestResults.filter(elem => elem.id !== id)
    }
    setLatestTestResults(updatedTestResults)
  }

  const createConfigurableBuilderTest = useCallback(
    async (input: ICreateConfigurableBuilderTestInput) => {
      try {
        const response = await createConfigurableBuilderTestMutation({
          variables: {
            input: { ...input, ...{ id: undefined } },
          },
        })
        let errors = response.data?.createConfigurableBuilderTest?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length > 0) {
          displayUserFeedback(errors[0].message, 'error')
        } else {
          if (builderId) {
            refetchTestData({ id: builderId })
          }
          const returnedTest = response.data?.createConfigurableBuilderTest?.configurableBuilderTest
          if (returnedTest) {
            setCurrentBuilderTest(returnedTest)
          }
          displayUserFeedback('Test saved successfully.', 'info')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createConfigurableBuilderTestMutation, builderId]
  )

  const updateConfigurableBuilderTest = useCallback(
    async (input: IUpdateConfigurableBuilderTestInput) => {
      try {
        const response = await updateConfigurableBuilderTestMutation({
          variables: {
            input,
          },
        })
        let errors = response.data?.updateConfigurableBuilderTest?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length > 0) {
          displayUserFeedback(errors[0].message, 'error')
        } else {
          if (builderId) {
            refetchTestData({ id: builderId })
          }
          const returnedTest = response.data?.updateConfigurableBuilderTest?.configurableBuilderTest
          if (returnedTest) {
            setCurrentBuilderTest(returnedTest)
            invalidateLatestTestResults(returnedTest.id)
            // TODO: automatically re-run this test instead of just invalidating.
          }
          displayUserFeedback('Test updated successfully.', 'info')
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateConfigurableBuilderTestMutation, latestTestResults]
  )

  const runConfigurableBuilderTests = useCallback(
    async (input: IRunConfigurableBuilderTestsInput) => {
      setTestsAreRunning(true)
      try {
        const response = await runConfigurableBuilderTestsMutation({
          variables: { input },
        })
        const responseData = response.data?.runConfigurableBuilderTests
        let errors = responseData?.errors || []
        errors = errors.filter(item => item.message)
        if (errors.length > 0) {
          displayUserFeedback(errors[0].message, 'error')
        } else {
          setLatestTestResults(responseData?.configurableBuilderTestResults || [])
        }
      } catch (e) {
        displayUserFeedback(e.message, 'error')
      } finally {
        setTestsAreRunning(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runConfigurableBuilderTestsMutation, testsAreRunning, setTestsAreRunning]
  )

  useEffect(() => {
    setConfigurableBuilderTests(testData?.configurableBuilderTests || [])
  }, [builderId, testData])

  return {
    configurableBuilderTests,
    createConfigurableBuilderTest,
    invalidateLatestTestResults,
    latestTestResults,
    updateConfigurableBuilderTest,
    runConfigurableBuilderTests,
    testsAreRunning,
  }
}
